<template>
	<section id="app-login">
		<div v-loading="loading" class="content">
			<section class="form">
				<div class="form-wrapper">
					<div v-id="'logo'" class="logo">
						<img :src="assets.logo" :alt="brand.companyName" :title="brand.companyName" />
					</div>

					<form name="login" @submit.prevent="login">
						<validation-summary v-if="loginErrors.length">
							<div v-for="error in loginErrors" :key="error" role="error">{{ error }}</div>
						</validation-summary>

						<form-section orientation="vertical">

							<control-group 
								v-id="'username'" 
								:valid="!$v.request.username.$error"
							>
								<div slot="title" class="control-label-row">
									<div class="control-label-text">Support Center username</div>
								</div>
								<input 
									v-control.block
									ref="username"
									v-model.trim="request.username"
                                    tabindex="1"
								/>
								<control-error v-if="$v.request.username.$error">Enter your username</control-error>
							</control-group>


							<control-group 
								v-id="'password'" 
								:valid="!$v.request.password.$error"
							>
								<div slot="title" class="control-label-row">
									<div class="control-label-text">Support Center password</div>
									<a href="https://support.pioneerrx.com/PersonForgotPassword.aspx" target="_blank" class="control-help-link">Forgot password?</a>
								</div>
								<input 
									v-control:password.block
									ref="password"
									v-model="request.password"
                                    tabindex="2"
								/>
								<control-error v-if="$v.request.password.$error">Enter your password</control-error>
							</control-group>


							<control-group v-id="'rememberme'">
								<checkbox
                                    v-model="request.rememberMe"
                                    :readonly="persistSession"
                                    name="rememberMe"
                                    label="Remember me"
                                    tabindex="3"
                                />

                                <checkbox
                                    v-if="allowPersistentSessions"
                                    v-model="persistSession"
                                    tabindex="4"
                                >
                                    Keep me signed in
                                    <control-info>
                                        To keep your account secure, use this option only on your personal devices.
                                    </control-info>
                                </checkbox>
							</control-group>

						</form-section>

						<nav>
							<btn
                                :disabled="loading"
                                type="submit"
                                primary
                                tabindex="4"
                            >
                                Sign in
                            </btn>
						</nav>
					</form>
				</div>
			</section>
		</div>
		<img class="banner" :src="assets.banner" />
	</section>
</template>


<script>
    import _ from 'lodash';
	import { required } from 'validators';
	import { AsyncMixin, FormValidationMixin } from 'pdk/mixins';
	import browser from 'pdk/services/browser';
    import auth from '@/services/auth';
    import AppConfig from '@/config';
	import { getters } from '@/store';

	export default {
		mixins: [AsyncMixin, FormValidationMixin],
		data() {
			return {
				request: {
					username: null,
					password: null,
                    rememberMe: false
				},
				assets: { 
					logo: require('../../assets/images/logo-pioneerrx.svg'),					
					banner: require('../../assets/images/auth/login-banner.jpg')
				},
				authError: this.$store.getters[getters.AUTH_ERROR],
                loginErrors: [],
                brand: AppConfig.brand,
                allowPersistentSessions: AppConfig.auth.allowPersistentSessions,
				persistSession: false
			};
		},
		validations: {
			request: {
				username: { required },
				password: { required }
			}
        },
        watch: {
            'request.persistSession'(value) {
                if (value === true) {
                    this.request.rememberMe = true;
                }
            }
        },
		mounted() {
			this.getRememberedUsername();

			if(!browser.getBrowser().mobile) {
				this.$nextTick(() => {
					if (this.request.rememberMe)
						this.$refs.password.focus();
					else
						this.$refs.username.focus();
				});
			}
			
			// If navigation occurred due to a prior authentication problem, display the message if one exists.
			if (this.authError)
				this.loginErrors[0] = this.authError;
		},
		methods: {
            login() {
				if (!this.validate(this.$v.request)) return;

				this.clearErrors();
				this.async(auth.authenticate(this.request))
                    .then((data) => {
						this.$auth.setTokens({
							accessToken: data.accessToken,
							refreshToken: data.refreshToken,
							persistSession: this.persistSession
						});

						if (this.request.rememberMe)
							localStorage.setItem('UN', this.request.username);

						if (this.$route.query.redirect)
							this.$router.push({ path: this.$route.query.redirect });
						else {
							const lastLocationGroupID = data.lastLocationGroupID;
							
							if (lastLocationGroupID) {
								this.$router.push({ name: 'group-dashboards', params: { locationGroupId: lastLocationGroupID } });
							}
							else {
								this.$router.push({ name: 'home' });
							}
						}
					})
                    .catch((error) => {
                        this.handleLoginErrors(error);

                        this.request.password = null;
                        setTimeout(() => {
                            this.$refs.password.focus();
					}, 0);
				});
			},
			getRememberedUsername() {
				var username = localStorage.getItem('UN');
				if (username) {
					this.request.username = username;
					this.request.rememberMe = true;
				}
			},
			clearErrors() {
				this.resetValidation();
				this.loginErrors = [];
			},
			handleLoginErrors(error) {
				this.loginErrors = [];

                // Try to show the user message if one was provided
                const message = _.get(error, 'response.data.message', undefined);
                if (message) {
                    this.loginErrors = [message];
                    return;
                }

                // We got a response back from the server
                if (error.response) {
					const responseData = error.response.data;
                    switch (responseData.serviceResult) {
                        case 'UsernamePasswordIncorrect':
                            this.loginErrors = ['The username or password you entered is incorrect.'];
                            break;

                        case 'EmailNotValidated':
                            this.loginErrors = ["Your account's email address has not been verified. Please see your administrator for more information."];
                            break;

                        case 'SMSPhoneNumberNotValidated':
                            this.loginErrors = ["Your account's phone number has not been verified. Please see your administrator for more information."];
                            break;

                        case 'PasswordResetRequired':
                            this.loginErrors = ['Your password has expired and needs to be reset. Please see your administrator for more information.'];
                            break;

                        case 'AccountDisabled':
                            this.loginErrors = ['Your account has been disabled. Please see your administrator for more information.'];
                            break;

                        default:
                            this.loginErrors = ["We are unable to sign you in at this time. You may be able to try again."];
                            break;
                    }
                }

                // The request was made, but the server didn't respond
                else if (error.request) {
                    this.loginErrors = ["We are unable to sign you in at this time. You may be able to try again."];
                }

                // Something went wrong while we were creating the request - the server was never contacted
                else {
                    this.loginErrors = ["We are unable to sign you in at this time. You may be able to try again."];
                }
			}
		},
	};
</script>


<style lang="scss">
@import '~@/assets/styles/references';


#app-login {
	width: 100%;
	display: flex;
	flex-direction: row;

	.content {
		flex: 1 1 auto;
		width: 61.8%;
		display: flex;
		flex-direction: column;
		align-items: stretch;
	}

	.form {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
		align-items: stretch;
	}
	.form-wrapper {
		flex: 1 0 auto;
		display: flex;
		flex-direction: column;
		margin: gx(3);
	}

	.control-label-row { display: flex; flex-direction: row; 
		.control-label-text { flex: 1 1 auto; }
		.control-help-link { flex: 0 0 auto; text-align: right; font-weight: $font-weight-normal; }
	}

	fieldset {
		margin-bottom: gx(1);
	}

	.logo {
		display: block;
		margin-bottom: gx(2);
		img {
			width: 165px; height: 54px;
		}
	}

	.app-input-container {
		width: 100%;
	}

	.register {
		flex: 1 0 auto;
		display: flex;
		flex-direction: column;
		align-items: center;
		background-color: #f8f8f8;
	}
	.register-wrapper {
		flex: 1 1 auto;
		color: color(black, secondary);
		padding: gx(1.5) gx(2);
	}

	.banner {
		flex: 0 1 auto;
		width: 38.2%;
		background-repeat: no-repeat;
		background-position: center center;
		background-size: cover;
	}

	@include media-breakpoint-down(sm) {
		.banner {
			display: none;
		}
	}
}
</style>
